import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async"; 
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Share2, Truck, RotateCcw, ShieldCheck, Star, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import StockBar from "@/components/StockBar";
import ProductCard from "@/components/ProductCard";
import SizeGuideModal from "@/components/SizeGuideModal";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

// ─── Star display ───────────────────────────────────────────────
function StarRow({ rating, size = "w-3 h-3" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= Math.round(rating) ? "fill-current text-brand-accent" : "text-border"}`}
        />
      ))}
    </div>
  );
}

// ─── Interactive star picker ─────────────────────────────────────
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="p-0.5 focus:outline-none"
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star
            className={`w-5 h-5 transition-colors ${n <= (hover || value) ? "fill-current text-brand-accent" : "text-border"}`}
          />
        </button>
      ))}
    </div>
  );
}

// ─── Rating breakdown bar ────────────────────────────────────────
function BreakdownBar({ star, count, total, onClick, active }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 w-full group ${active ? "opacity-100" : "opacity-70 hover:opacity-100"}`}
    >
      <span className="ie-overline text-text-secondary w-8 text-right">{star} ★</span>
      <div className="flex-1 h-1 bg-border overflow-hidden">
        <div
          className="h-full bg-brand-accent transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="ie-overline text-text-secondary w-8">{pct}%</span>
    </button>
  );
}

// ─── Write / edit review form ────────────────────────────────────
function ReviewForm({ productId, existing, onSuccess, onCancel }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [title, setTitle] = useState(existing?.title || "");
  const [body, setBody] = useState(existing?.body || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please select a rating.");
    if (title.trim().length < 3) return toast.error("Title must be at least 3 characters.");
    if (body.trim().length < 10) return toast.error("Review must be at least 10 characters.");
    setLoading(true);
    try {
      if (existing) {
        await api.patch(`/reviews/${existing.id}`, { rating, title, body });
        toast.success("Review updated.");
      } else {
        await api.post("/reviews", { product_id: productId, rating, title, body });
        toast.success("Review posted.");
      }
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-border p-6 space-y-5" data-testid="review-form">
      <div className="ie-overline text-brand-accent mb-1">
        {existing ? "Edit your review" : "Write a review"}
      </div>

      <div className="space-y-1">
        <div className="ie-overline text-text-secondary">Your rating</div>
        <StarPicker value={rating} onChange={setRating} />
      </div>

      <div className="space-y-1">
        <label className="ie-overline text-text-secondary" htmlFor="r-title">Title</label>
        <input
          id="r-title"
          className="ie-input w-full"
          placeholder="Summarise your experience"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
        />
      </div>

      <div className="space-y-1">
        <label className="ie-overline text-text-secondary" htmlFor="r-body">Your review</label>
        <textarea
          id="r-body"
          className="ie-input w-full min-h-[100px] resize-y"
          placeholder="What did you love? What could be better?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
        />
        <div className="text-xs text-text-secondary text-right">{body.length} / 2000</div>
      </div>

      <div className="flex gap-3 pt-1">
        <button
          type="submit"
          disabled={loading}
          className="ie-btn-primary"
          data-testid="submit-review"
        >
          {loading ? "Posting…" : existing ? "Update review" : "Post review"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="ie-btn-ghost">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

// ─── Single review card ──────────────────────────────────────────
function ReviewCard({ review, currentUserId, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const isOwn = currentUserId && review.user_id === currentUserId;

  const handleDelete = async () => {
    if (!window.confirm("Delete your review?")) return;
    setDeleting(true);
    try {
      await api.delete(`/reviews/${review.id}`);
      toast.success("Review deleted.");
      onDeleted(review.id);
    } catch {
      toast.error("Could not delete review.");
      setDeleting(false);
    }
  };

  return (
    <div className="border-b border-border pb-8" data-testid={`review-card-${review.id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <StarRow rating={review.rating} />
          <span className="ie-overline text-text-secondary">{review.created_at?.slice(0, 10)}</span>
          {isOwn && (
            <span className="ie-overline text-brand-accent">· you</span>
          )}
        </div>
        {isOwn && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="text-text-secondary hover:text-foreground transition-colors"
            aria-label="Delete review"
            data-testid={`delete-review-${review.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <h4 className="font-serif text-xl mb-2">{review.title}</h4>
      <p className="text-sm text-text-secondary leading-relaxed mb-3">{review.body}</p>
      <div className="ie-overline text-foreground">— {review.user_name}</div>
    </div>
  );
}

// ─── Reviews section ─────────────────────────────────────────────
function ReviewsSection({ product, onSummaryUpdate }) {
  const { user } = useAuth();

  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("newest");
  const [filterStar, setFilterStar] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const PAGE_SIZE = 5;

  const userReview = reviews.find((r) => r.user_id === user?.id);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get(`/reviews/summary/${product.id}`);
      setSummary(data);
      onSummaryUpdate?.(data);
    } catch {
      // non-fatal
    }
  }, [product.id, onSummaryUpdate]);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        sort_by: sort,
        ...(filterStar > 0 && { filter_star: filterStar }),
      };
      const { data } = await api.get(`/reviews/product/${product.id}`, { params });
      setReviews(data.reviews);
      setTotal(data.total);
    } catch {
      toast.error("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  }, [product.id, page, sort, filterStar]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleReviewSuccess = () => {
    setShowForm(false);
    setPage(1);
    fetchSummary();
    fetchReviews();
  };

  const handleDeleted = (deletedId) => {
    setReviews((prev) => prev.filter((r) => r.id !== deletedId));
    setTotal((t) => t - 1);
    fetchSummary();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const avgRating = summary?.average_rating ?? product.rating;
  const totalReviews = summary?.total_reviews ?? product.review_count;

  return (
    <section className="border-t border-border bg-surface" data-testid="reviews-section">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-20">

        {/* Header row */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-10 mb-12">
          <div className="md:col-span-4">
            <div className="ie-overline text-brand-accent mb-3">Said Quietly</div>
            <h2 className="font-serif text-4xl tracking-tight leading-tight">
              {avgRating.toFixed(1)} of 5
            </h2>
            <div className="text-sm text-text-secondary mt-1 mb-5">
              {totalReviews.toLocaleString("en-IN")} verified reviews
            </div>
            <StarRow rating={avgRating} size="w-4 h-4" />

            {/* Breakdown bars */}
            {summary?.breakdown && (
              <div className="mt-6 space-y-2">
                {summary.breakdown.map((b) => (
                  <BreakdownBar
                    key={b.star}
                    star={b.star}
                    count={b.count}
                    total={totalReviews}
                    active={filterStar === b.star}
                    onClick={() => {
                      setFilterStar(filterStar === b.star ? 0 : b.star);
                      setPage(1);
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sort + filter controls */}
          <div className="md:col-span-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex gap-1">
                {["newest", "highest", "lowest"].map((s) => (
                  <button
                    key={s}
                    onClick={() => { setSort(s); setPage(1); }}
                    className={`ie-overline px-3 py-1.5 border transition-colors ${
                      sort === s
                        ? "border-foreground bg-foreground text-background"
                        : "border-border text-text-secondary hover:border-foreground"
                    }`}
                  >
                    {s === "newest" ? "Newest" : s === "highest" ? "Highest rated" : "Lowest rated"}
                  </button>
                ))}
              </div>

              {/* Write review CTA */}
              {user ? (
                !userReview && !showForm && (
                  <button
                    onClick={() => setShowForm(true)}
                    className="ie-btn-primary"
                    data-testid="write-review-btn"
                  >
                    Write a review
                  </button>
                )
              ) : (
                <Link
                  to="/login"
                  className="ie-overline text-text-secondary hover:text-foreground underline underline-offset-2"
                  data-testid="login-to-review"
                >
                  Sign in to review
                </Link>
              )}
            </div>

            {/* Write / edit form */}
            {showForm && !userReview && (
              <div className="mb-8">
                <ReviewForm
                  productId={product.id}
                  onSuccess={handleReviewSuccess}
                  onCancel={() => setShowForm(false)}
                />
              </div>
            )}

            {/* Active filter pill */}
            {filterStar > 0 && (
              <div className="flex items-center gap-2 mb-6">
                <span className="ie-overline text-text-secondary">Showing {filterStar}-star reviews</span>
                <button
                  onClick={() => { setFilterStar(0); setPage(1); }}
                  className="ie-overline text-brand-accent underline underline-offset-2"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Reviews list */}
            {loading ? (
              <div className="space-y-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="border-b border-border pb-8 space-y-3 animate-pulse">
                    <div className="h-3 w-28 bg-muted" />
                    <div className="h-5 w-48 bg-muted" />
                    <div className="h-3 w-full bg-muted" />
                    <div className="h-3 w-3/4 bg-muted" />
                  </div>
                ))}
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-text-secondary text-sm italic py-8">
                {filterStar > 0
                  ? `No ${filterStar}-star reviews yet.`
                  : "No reviews yet — be the first to write one."}
              </div>
            ) : (
              <div className="space-y-8">
                {reviews.map((r) => (
                  <ReviewCard
                    key={r.id}
                    review={r}
                    currentUserId={user?.id}
                    onDeleted={handleDeleted}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center gap-4 mt-10 pt-6 border-t border-border">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="ie-btn-ghost p-2 disabled:opacity-30"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="ie-overline text-text-secondary">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="ie-btn-ghost p-2 disabled:opacity-30"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <span className="ie-overline text-text-secondary ml-auto">
                  {total.toLocaleString("en-IN")} reviews
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main page ───────────────────────────────────────────────────
export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState("");
  const [pin, setPin] = useState("");
  const [eta, setEta] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem, setDrawerOpen } = useCart();
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const { has, toggle } = useWishlist();
  const [liveSummary, setLiveSummary] = useState(null);

  useEffect(() => {
    setProduct(null);
    setLiveSummary(null);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setActiveImg(0);
      setSize("");
      setColor(data.colors?.[0] || null);
    });
    api.get("/products", { params: { limit: 8 } }).then(({ data }) => {
      setRelated((data.items || []).filter((p) => p.id !== id).slice(0, 4));
    });
  }, [id]);

  const handleAdd = () => {
    if (!size) return toast.error("Please choose a size");
    if (!color) return toast.error("Please choose a colour");
    addItem(product, size, 1, color);
  };

  const checkPin = (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(pin)) {
      setEta("Enter a valid 6-digit PIN code.");
      return;
    }
    const days = 2 + (parseInt(pin[0]) % 4);
    const date = new Date(Date.now() + days * 86400000);
    const fmt = date.toLocaleDateString("en-IN", { weekday: "long", month: "long", day: "numeric" });
    setEta(`Delivers by ${fmt}`);
  };

  if (!product) {
    return (
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-10">
        <div className="aspect-[3/4] bg-muted animate-pulse" />
        <div className="space-y-4">
          <div className="h-4 w-32 bg-muted animate-pulse" />
          <div className="h-12 bg-muted animate-pulse" />
          <div className="h-6 w-40 bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  
  const inWishlist = has(product.id);

const productUrl = `https://induseirene.in/product/${product.id}`;

const seoTitle = `${product.name} | INDUS EIRENE`;

const seoDescription =
  product.description ||
  `Shop ${product.name} from INDUS EIRENE. Premium ${product.product_type || "clothing"} designed with timeless style and modern comfort.`;

const seoImage = product.images?.[0] || "https://induseirene.in/favicon.png";

const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  description: seoDescription,
  image: product.images || [seoImage],
  url: productUrl,
  brand: {
    "@type": "Brand",
    name: "INDUS EIRENE",
  },
  offers: {
    "@type": "Offer",
    url: productUrl,
    priceCurrency: "INR",
    price: product.price,
    availability:
      product.stock > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
  },
};


  return (
    <div data-testid="product-detail-page">
      <Helmet>
  <title>{seoTitle}</title>

  <meta
    name="description"
    content={seoDescription.slice(0, 160)}
  />

  <meta
    name="robots"
    content="index, follow"
  />

  <link
    rel="canonical"
    href={productUrl}
  />
 <script type="application/ld+json">
  {JSON.stringify(productSchema)}
</script>

  {/* Open Graph */}
  <meta
    property="og:type"
    content="product"
  />

  <meta
    property="og:url"
    content={productUrl}
  />

  <meta
    property="og:title"
    content={seoTitle}
  />

  <meta
    property="og:description"
    content={seoDescription.slice(0, 160)}
  />

  <meta
    property="og:image"
    content={seoImage}
  />

  <meta
    property="og:site_name"
    content="INDUS EIRENE"
  />

  {/* Twitter / X */}
  <meta
    name="twitter:card"
    content="summary_large_image"
  />

  <meta
    name="twitter:title"
    content={seoTitle}
  />

  <meta
    name="twitter:description"
    content={seoDescription.slice(0, 160)}
  />

  <meta
    name="twitter:image"
    content={seoImage}
  />
  
</Helmet>
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-8 md:py-16">
        {/* Breadcrumb */}
        <nav className="ie-overline text-text-secondary mb-6 md:mb-8" data-testid="breadcrumb">
          <Link to="/shop" className="hover:text-foreground">Atelier</Link>
          <span className="mx-2 opacity-50">/</span>
          <Link to={`/shop/${product.gender}`} className="hover:text-foreground">{product.gender}</Link>
          <span className="mx-2 opacity-50">/</span>
          <span className="text-foreground">{product.product_type}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14">
          {/* Gallery */}
          <div className="md:col-span-7 space-y-6">
            <motion.div
              key={activeImg}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="aspect-[3/4] bg-surface overflow-hidden"
              data-testid="product-main-image"
            >
              <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
            </motion.div>

            <div className="grid grid-cols-4 gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border ${i === activeImg ? "border-foreground" : "border-transparent hover:border-border"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Details — sticky */}
          <div className="md:col-span-5">
            <div className="sticky top-28 space-y-7">
              <div>
                <div className="ie-overline text-brand-accent mb-2">{product.subtitle}</div>
                <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05]" data-testid="product-name">{product.name}</h1>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1" data-testid="product-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(liveSummary?.average_rating ?? product.rating) ? "fill-current text-brand-accent" : "text-border"}`} />
                  ))}
                </div>
                <span className="text-xs text-text-secondary">
                  {(liveSummary?.average_rating ?? product.rating).toFixed(1)} · {(liveSummary?.total_reviews ?? product.review_count).toLocaleString("en-IN")} reviews
                </span>
              </div>

              <div className="flex items-baseline gap-3" data-testid="product-price">
                <span className="font-serif text-3xl">₹{product.price.toLocaleString("en-IN")}</span>
                {product.compare_at_price && (
                  <>
                    <span className="text-base text-text-secondary line-through">₹{product.compare_at_price.toLocaleString("en-IN")}</span>
                    <span className="ie-overline text-brand-accent">
                      Save ₹{(product.compare_at_price - product.price).toLocaleString("en-IN")}
                    </span>
                  </>
                )}
              </div>
              <div className="ie-overline text-text-secondary -mt-3">Inclusive of all taxes</div>

              <p className="text-sm text-text-secondary leading-relaxed">{product.description}</p>

              {/* Color */}
              {product.colors?.length > 0 && (
                <div>
                  <div className="ie-overline mb-3">Colour · {color || product.colors[0]}</div>
                  <div className="flex gap-2">
                    {product.colors.map((c, index) => (
                      <button
                        key={c}
                       onClick={() => {
                               setColor(c);
             if (product.color_images?.[c]) 
              {
                   const colorImage = product.color_images[c];
                    const imageIndex = product.images?.indexOf(colorImage);

             if (imageIndex >= 0) 
              {
                setActiveImg(imageIndex);
              }
              } else if (product.images?.[index]) {
                       setActiveImg(index);
                       }
              }}
                        className={`w-9 h-9 border cursor-pointer ${
                          color === c
                            ? "border-foreground bg-foreground text-background"
                            : "border-border"
                        } flex items-center justify-center text-[10px]`}
                        title={c}
                        data-testid={`color-${c.toLowerCase()}`}
                      >
                        {c[0]}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="ie-overline">Size</div>
                  <button
                    onClick={() => setSizeGuideOpen(true)}
                    className="ie-overline text-text-secondary hover:text-foreground underline underline-offset-2"
                    data-testid="size-guide-link"
                  >
                    Size Guide
                  </button>
                  <SizeGuideModal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={`min-w-[48px] h-12 px-3 border text-sm transition-colors ${size === s ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
                      data-testid={`size-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stock */}
              <StockBar stock={product.stock} />

              {/* Actions */}
              <div className="grid grid-cols-1 gap-3 pt-2">
                <button onClick={handleAdd} className="ie-btn-primary justify-center" data-testid="add-to-cart">
                  Add to Bag
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => toggle(product)}
                    className={`ie-btn-ghost justify-center gap-2 ${inWishlist ? "border-brand-accent text-brand-accent" : ""}`}
                    data-testid="wishlist-button"
                  >
                    <Heart className={`w-3.5 h-3.5 ${inWishlist ? "fill-current" : ""}`} />
                    {inWishlist ? "Saved" : "Save"}
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }}
                    className="ie-btn-ghost justify-center gap-2"
                    data-testid="share-button"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                </div>
              </div>

              {/* Delivery estimator */}
              <div className="border-t border-border pt-6">
                <div className="ie-overline mb-3">Delivery Estimator</div>
                <form onSubmit={checkPin} className="flex gap-2 items-end">
                  <input
                    type="text"
                    placeholder="Enter PIN code"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="ie-input flex-1"
                    data-testid="delivery-pin-input"
                  />
                  <button className="ie-overline hover:text-brand-accent pb-3" data-testid="delivery-pin-check">Check</button>
                </form>
                {eta && <div className="text-xs text-foreground mt-3" data-testid="delivery-eta">{eta}</div>}
              </div>

              {/* Trust */}
              <div className="border-t border-border pt-6 grid grid-cols-3 gap-4 text-[11px] text-text-secondary">
                <div className="flex flex-col items-start gap-2"><Truck className="w-4 h-4 text-foreground" />Free shipping over ₹3,500</div>
                <div className="flex flex-col items-start gap-2"><RotateCcw className="w-4 h-4 text-foreground" />7-day atelier replacement</div>
                <div className="flex flex-col items-start gap-2"><ShieldCheck className="w-4 h-4 text-foreground" />Authenticity guaranteed</div>
              </div>

              {/* Accordion details */}
              <Accordion type="single" collapsible className="border-t border-border pt-2">
                <AccordionItem value="fabric" className="border-b border-border" data-testid="accordion-fabric">
                  <AccordionTrigger className="ie-overline hover:no-underline">Fabric & Finish</AccordionTrigger>
                  <AccordionContent className="text-sm text-text-secondary leading-relaxed">{product.fabric}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="care" className="border-b border-border" data-testid="accordion-care">
                  <AccordionTrigger className="ie-overline hover:no-underline">Care</AccordionTrigger>
                  <AccordionContent className="text-sm text-text-secondary leading-relaxed">{product.care}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="shipping" className="border-b border-border" data-testid="accordion-shipping">
                  <AccordionTrigger className="ie-overline hover:no-underline">Shipping & Returns</AccordionTrigger>
                  <AccordionContent className="text-sm text-text-secondary leading-relaxed">
                    Complimentary shipping on orders above ₹3,500. 7-day atelier replacement. Each piece is inspected by hand before dispatch.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      {/* Live reviews section */}
      <ReviewsSection product={product} onSummaryUpdate={setLiveSummary} />

      {/* Frequently bought together */}
      {related.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-16 md:py-20" data-testid="related-section">
          <div className="ie-overline text-brand-accent mb-3">Frequently Composed With</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight mb-8 md:mb-10">Pair quietly.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}